export const SimulationDetailsDefaultTitles = [
  'Simulation Overview',
  'About the Persona',
  'Scoring Criteria',
];

export interface SimBuilderTab {
  id: string;
  label: string;
}

export enum SimBuilderTabValues {
  // Labels (transloco keys — resolved via the `transloco` pipe in the template)
  GeneralLabel      = 'simulationBuilder.TAB_GENERAL',
  ContentsLabel     = 'simulationBuilder.TAB_CONTENTS',
  KeyWordsLabel     = 'simulationBuilder.TAB_KEY_WORDS',
  ScoringLabel      = 'simulationBuilder.TAB_SCORING',
  TestingLabel      = 'simulationBuilder.TAB_TESTING',
  PromptsLabel      = 'simulationBuilder.TAB_PROMPTS',
  WhatUsersSeeLabel = 'simulationBuilder.TAB_WHAT_USERS_SEE',
  QuestionsLabel    = 'simulationBuilder.TAB_QUESTIONS',

  // IDs
  GeneralId      = 'uniqueId1',
  ContentsId     = 'uniqueId2',
  KeyWordsId     = 'uniqueId3',
  ScoringId      = 'uniqueId4',
  TestingId      = 'uniqueId5',
  WhatUsersSeeId = 'uniqueId6',
  QuestionsId    = 'uniqueId7',
}

const SIM_BUILDER_TABS = {
  general:      { id: SimBuilderTabValues.GeneralId,      label: SimBuilderTabValues.GeneralLabel },
  contents:     { id: SimBuilderTabValues.ContentsId,     label: SimBuilderTabValues.ContentsLabel },
  keyWords:     { id: SimBuilderTabValues.KeyWordsId,     label: SimBuilderTabValues.KeyWordsLabel },
  scoring:      { id: SimBuilderTabValues.ScoringId,      label: SimBuilderTabValues.ScoringLabel },
  testing:      { id: SimBuilderTabValues.TestingId,      label: SimBuilderTabValues.TestingLabel },
  whatUsersSee: { id: SimBuilderTabValues.WhatUsersSeeId, label: SimBuilderTabValues.WhatUsersSeeLabel },
  questions:    { id: SimBuilderTabValues.QuestionsId,    label: SimBuilderTabValues.QuestionsLabel },
} satisfies Record<string, SimBuilderTab>;

export interface SimBuilderTabsContext {
  roles: string[];
  // SELF_AUTHORING_ROLEPLAY — the single flag gating the self-authoring surface.
  isSelfAuthoringRoleplayEnabled: boolean;
  isAuthor: boolean;
  showKeyWords: boolean;
}

export function getSimBuilderTabs(context: SimBuilderTabsContext): SimBuilderTab[] {
  const isQcAdmin = context.roles.includes('QCAdmin');

  // "What users see" reads generated call details that only exist for simulations
  // created via the AI self-authoring roleplay generation flow, so it is limited to
  // authors on the two self-authoring paths below.
  const showWhatUsersSee = context.isAuthor;

  if (context.isSelfAuthoringRoleplayEnabled && isQcAdmin) {
    return [
      SIM_BUILDER_TABS.general,
      SIM_BUILDER_TABS.scoring,
      SIM_BUILDER_TABS.keyWords,
      { ...SIM_BUILDER_TABS.contents, label: SimBuilderTabValues.PromptsLabel },
      SIM_BUILDER_TABS.testing,
      ...(showWhatUsersSee ? [SIM_BUILDER_TABS.whatUsersSee] : []),
    ];
  }

  if (context.isSelfAuthoringRoleplayEnabled) {
    return [
      SIM_BUILDER_TABS.general,
      SIM_BUILDER_TABS.scoring,
      SIM_BUILDER_TABS.testing,
      ...(showWhatUsersSee ? [SIM_BUILDER_TABS.whatUsersSee] : []),
    ];
  }

  return [
    SIM_BUILDER_TABS.general,
    SIM_BUILDER_TABS.contents,
    ...(context.showKeyWords ? [SIM_BUILDER_TABS.keyWords] : []),
    SIM_BUILDER_TABS.scoring,
    SIM_BUILDER_TABS.testing,
  ];
}

// Tabs for a Knowledge Check (GUIDED) call, shown when SELF_AUTHORING_KNOWLEDGE_CHECK is on.
export function getKnowledgeCheckTabs(): SimBuilderTab[] {
  return [
    SIM_BUILDER_TABS.general,
    SIM_BUILDER_TABS.questions,
    SIM_BUILDER_TABS.whatUsersSee,
    SIM_BUILDER_TABS.testing,
  ];
}

export interface ConversePromptValidationContext {
  isSelfAuthoringRoleplayEnabled: boolean;
  hasSimulationGeneration: boolean | undefined;
  isAuthor: boolean;
  hasQcAdminRole: boolean;
  hasConversePrompt: boolean | undefined;
}

export function shouldSkipConversePromptValidation(context: ConversePromptValidationContext): boolean {
  return (
    context.isSelfAuthoringRoleplayEnabled &&
    context.hasSimulationGeneration === true &&
    context.isAuthor &&
    !context.hasQcAdminRole &&
    context.hasConversePrompt === true
  );
}
